"use client";

import { ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { cdnImageUrlFromPath } from "@/lib/design-search/cdn-image";
import { HuntImageThumbnail } from "@/components/hunt-image-thumbnail";

interface HuntSubmissionSummaryCardProps {
  index: number;
  itemName: string;
  patentId: string;
  inputImageUrl?: string | null;
  patentCdnPath?: string | null;
}

export function HuntSubmissionSummaryCard({
  index,
  itemName,
  patentId,
  inputImageUrl,
  patentCdnPath,
}: HuntSubmissionSummaryCardProps) {
  const patentImageUrl = patentCdnPath ? cdnImageUrlFromPath(patentCdnPath) : null;

  return (
    <Card
      className={cn(
        "rounded-2xl border-green-500 bg-green-50 shadow-sm dark:bg-green-950/20",
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
            {index}
          </span>
          <CardTitle className="min-w-0 flex-1 text-lg leading-tight">{itemName}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {inputImageUrl || patentImageUrl ? (
          <div className="flex items-center gap-2">
            {inputImageUrl ? (
              <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <HuntImageThumbnail src={inputImageUrl} alt="Your search photo" />
                <span className="text-xs font-medium text-muted-foreground">Your photo</span>
              </div>
            ) : (
              <div className="min-w-0 flex-1" />
            )}

            <div className="flex shrink-0 flex-col items-center gap-0.5 px-1">
              <ArrowRight className="h-5 w-5 text-green-500" strokeWidth={2.5} />
            </div>

            {patentImageUrl ? (
              <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <HuntImageThumbnail src={patentImageUrl} alt={`Patent ${patentId}`} />
                <span className="truncate text-xs font-bold tabular-nums text-green-600 dark:text-green-400">
                  {patentId}
                </span>
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5">
                <p className="text-base font-bold text-green-600 dark:text-green-400">{patentId}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-base text-green-600 dark:text-green-400">
            Found <span className="font-bold">{patentId}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
